import "./OutcomeInfo.scss";

import clsx from "clsx";

import type { SectionOutcomeIndexData, SectionOutcomeIndexPage } from "api/api-types-assessment";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { NestedSectionOutcomeType } from "store/selectors";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";

/**
 * The number of pages that must be present before the topics start being collapsed into an accordion.
 */
const MAX_PAGE_AMOUNT_BEFORE_ACCORDION = 6;

/**
 * Displays a list of all pages that are attached to the course outcome,
 * organized by category with simple titles.
 */
export const OutcomeUseIndex = ({
  sectionOutcome,
  indexData,
}: {
  sectionOutcome: NestedSectionOutcomeType;
  indexData: SectionOutcomeIndexData;
}) => {
  const topicPages = indexData.topics.flatMap((topic) => topic.pages);
  const assignmentPages = indexData.assignments;
  const resourcePages = indexData.course_resources;
  const pagesForOutcomeCount = topicPages.length + assignmentPages.length + resourcePages.length;

  return (
    <div>
      <h2 className="h3 m-0 mb-3 text-black-tint-40">{t("outcomes.usage.title")}</h2>
      {!pagesForOutcomeCount && (
        <div className="body-m rounded p-4">
          {t("outcomes.usage.not_using_yet", { outcome_title: sectionOutcome.outcome.title })}
        </div>
      )}

      {pagesForOutcomeCount > 0 && (
        <div className="space-y-4">
          {topicPages.length > 0 && (
            <div>
              <h3 className="h3 m-0 mb-2">
                {t("glossary.topics")} ({topicPages.length})
              </h3>
              <div className="mt-2">
                {indexData.topics.map((topicRow) => (
                  <AccordionTitle
                    className="text-black-tint-40"
                    key={topicRow.id}
                    startOpen={topicPages.length < MAX_PAGE_AMOUNT_BEFORE_ACCORDION}
                    title={<span className="text-black-tint-10">{topicRow.title}</span>}
                  >
                    {topicRow.pages.map((pageRow) => (
                      <OutcomeUseListPageItem
                        className="my-2"
                        href={pageUrl(sectionOutcome.section_id, pageRow.page_id)}
                        key={pageRow.page_id}
                        pageRow={pageRow}
                      />
                    ))}
                  </AccordionTitle>
                ))}
              </div>
            </div>
          )}

          {assignmentPages.length > 0 && (
            <>
              <h3 className="h3 m-0 mb-2 text-black-tint-10">
                {t("glossary.assignments")} ({assignmentPages.length})
              </h3>
              <div className="mt-2">
                {assignmentPages.map((pageRow) => (
                  <OutcomeUseListPageItem
                    className="mb-2"
                    href={pageUrl(sectionOutcome.section_id, pageRow.page_id)}
                    key={pageRow.page_id}
                    pageRow={pageRow}
                  />
                ))}
              </div>
            </>
          )}

          {resourcePages.length > 0 && (
            <div>
              <h3 className="h3 m-0 mb-2 text-black-tint-10">
                {t("glossary.course_resources")} ({resourcePages.length})
              </h3>
              <div className="mt-2">
                {resourcePages.map((pageRow) => (
                  <OutcomeUseListPageItem
                    className="mb-2"
                    href={pageUrl(sectionOutcome.section_id, pageRow.page_id)}
                    key={pageRow.page_id}
                    pageRow={pageRow}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const OutcomeUseListPageItem = ({
  pageRow,
  href,
  className,
}: {
  pageRow: SectionOutcomeIndexPage;
  href: string;
  className?: string;
}) => {
  return (
    <div className={clsx("body-m flex items-center justify-between", className)}>
      <Link to={href}>{pageRow.title}</Link>

      {pageRow.max_points! > 0 && (
        <div className="text-black-tint-40">
          {t("rubric_card.assessment_points_other", { count: pageRow.max_points! })}
        </div>
      )}
    </div>
  );
};
